import type { RouteType } from "../../../types/RouteType";
import { ManholeViewStore } from "./stores/ManholeViewStore";

const ManholeRoute: RouteType = {
  name: "Manhole Designer",
  path: "/production/manhole/:tab?",
  defaultParams: { tab: "design" },
  layout: "/app",
  requiresAuth: true,
  requiresPermission: false,
  viewportCard: {
    header: {
      component: () => import("./components/ManholeHeader"),
      title: "Manhole Stack Designer",
      color: "primary",
      enableRefreshButton: true,
      enableBackButton: false,
    },
    body: {
      component: () => import("./components/ManholeBody"),
      grayBackground: true,
    },
  },

  beforeEnter: async ({ rootStore, params }) => {
    try {
      // Load accounts for dropdown selection
      if ((rootStore as any).accountsStore) {
        await (rootStore as any).accountsStore.find();
      }

      // Load catalog products if not already loaded
      if ((rootStore as any).manholeComponentsStore?.entities.length === 0) {
        await (rootStore as any).manholeComponentsStore.find();
      }

      // If no components found, try loading from products service with filter
      if ((rootStore as any).manholeComponentsStore?.entities.length === 0 && (rootStore as any).productsStore) {
        await (rootStore as any).productsStore.find();
      }

      // Load user's projects
      await (rootStore as any).manholeProjectsStore.find();

      // If projectId provided, ensure it's loaded with its pipes
      if (params?.projectId) {
        const project = (rootStore as any).manholeProjectsStore?.entities?.find(
          (p: any) => p.id === params.projectId || p.projectId === params.projectId
        );

        if (!project) {
          await (rootStore as any).manholeProjectsStore.get(params.projectId);
        }

        // Load pipes from separate service
        if ((rootStore as any).manholePipeConnectionsStore) {
          await (rootStore as any).manholePipeConnectionsStore.find({
            query: {
              manholeProjectId: params.projectId,
              $sort: { pipeNumber: 1 }
            }
          });
        }
      }

      return true;
    } catch (error) {
      console.error("Failed to load manhole designer data:", error);
      return false;
    }
  },

  onEnter: ({ rootStore, params }) => {
    const viewStore = (rootStore as any).manholeViewStore as ManholeViewStore;

    // Set active project if projectId provided
    if (params?.projectId) {
      viewStore.setActiveProject(params.projectId as string);
    }

    // Set current tab
    if (params?.tab && (params.tab === 'design' || params.tab === 'output')) {
      // Handle tab switching if needed
    }
  },

  onParamsChange: async ({ rootStore, params, previousParams }) => {
    const viewStore = (rootStore as any).manholeViewStore as ManholeViewStore;

    // Handle project change
    if (params?.projectId !== previousParams?.projectId) {
      if (params?.projectId) {
        const project = (rootStore as any).manholeProjectsStore?.entities?.find(
          (p: any) => p.id === params.projectId || p.projectId === params.projectId
        );

        if (!project) {
          await (rootStore as any).manholeProjectsStore.get(params.projectId);
        }

        // Load pipes from separate service when project changes
        if ((rootStore as any).manholePipeConnectionsStore) {
          await (rootStore as any).manholePipeConnectionsStore.find({
            query: {
              manholeProjectId: params.projectId,
              $sort: { pipeNumber: 1 }
            }
          });
        }

        viewStore.setActiveProject(params.projectId as string);
      } else {
        viewStore.setActiveProject(null);
      }
    }
  },

  beforeExit: ({ rootStore }) => {
    const viewStore = (rootStore as any).manholeViewStore as ManholeViewStore;

    // Warn if there are unsaved changes
    if (viewStore.isDirty) {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to leave?"
      );
      return confirmed;
    }

    return true;
  },

  onExit: ({ rootStore }) => {
    const viewStore = (rootStore as any).manholeViewStore as ManholeViewStore;
    viewStore.cleanup();
  },

  dialogs: [
    {
      name: "SaveProjectDialog",
      component: () => import("./dialogs/SaveProjectDialog"),
      title: "Save Manhole Project",
      cancelButton: true,
      onSubmit: async ({ rootStore }) => {
        const viewStore = (rootStore as any).manholeViewStore as ManholeViewStore;
        const formData = viewStore.dialogFormData;

        viewStore.updateEditingProject(formData);
        await viewStore.saveProject();

        if (!viewStore.error) {
          viewStore.clearDialogFormData();
          rootStore.routerStore.closeDialog();
          return true;
        }

        return false;
      },
      onSubmitValidation: ({ rootStore }) => {
        const viewStore = (rootStore as any).manholeViewStore as ManholeViewStore;
        const formData = viewStore.dialogFormData;

        // Check that required fields have actual values (not empty strings)
        const hasAccount = formData?.accountId && formData.accountId !== '';
        const hasStructureId = formData?.structureId && formData.structureId.trim() !== '';

        return Boolean(hasAccount && hasStructureId);
      },
    },
    {
      name: "LoadProjectDialog",
      component: () => import("./dialogs/LoadProjectDialog"),
      title: "Load Manhole Project",
      height: 400,
      cancelButton: true,
      submitButtonText: "" // Handle selection internally
    },
    {
      name: "ExportPDFDialog",
      component: () => import("./dialogs/ExportPDFDialog"),
      title: "Export to PDF",
      cancelButton: true,
      onSubmit: async ({ rootStore }) => {
        const viewStore = (rootStore as any).manholeViewStore as ManholeViewStore;
        const formData = (rootStore as any).routerStore?.dialogFormData || {};

        // Generate PDF (implemented in Phase 6)
        try {
          const { generatePDF } = await import("./utils/pdfGenerator");
          await generatePDF(viewStore, formData);
          rootStore.routerStore.closeDialog();
          return true;
        } catch (error) {
          console.error("PDF generation failed:", error);
          return false;
        }
      },
    },
  ],
};

export default ManholeRoute;